import { useEffect, useRef, useState } from "react";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { authedFetch } from "../services/authedFetch";
import { getActiveTenantId, getActiveSiteId } from "../services/activeTenant";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const COUNTDOWN_START = 90;

function TypingDots() {
  return (
    <span className="inline-flex gap-1 align-middle">
      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500/80 animate-bounce [animation-delay:-0.2s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-violet-500/80 animate-bounce [animation-delay:-0.1s]" />
      <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500/80 animate-bounce" />
    </span>
  );
}

export default function AIChatPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);

  const endRef = useRef(null);
  const textRef = useRef(null);
  const navigate = useNavigate();

  // Smooth autoscroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  // Countdown
  useEffect(() => {
    let timer;
    if (busy) {
      setRemaining(COUNTDOWN_START);
      timer = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    }
    return () => clearInterval(timer);
  }, [busy]);

  const progressWidth = `${((COUNTDOWN_START - remaining) / COUNTDOWN_START) * 100}%`;

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setBusy(true);

    try {
      // chatWithAI is gated server-side (it reads a clinical note and spends on
      // the AI key), so the call must prove who is asking AND which site they
      // are asking as. This page sits behind RequireAuth, so there is always a
      // signed-in user by the time this runs.
      const res = await authedFetch(
        `${FUNCTIONS_BASE}/chatWithAI`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text,
            tenantId: getActiveTenantId(),
            siteId: getActiveSiteId(),
          }),
        }
      );
      if (!res.ok) throw new Error(`chatWithAI returned ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data?.reply ?? "…" },
      ]);
      const notificationSound = new Audio("/notify.mp3");
      notificationSound.play();
    } catch (err) {
      console.error("❌ Error sending message:", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "⚠️ There was an error processing your request." },
      ]);
    }

    setBusy(false);
  };

  return (
    <div
      className="h-screen w-full flex bg-fixed"
      style={{
        backgroundImage: "url('/bg_ai.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Subtle color-tinted overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/70 via-white/55 to-fuchsia-50/70 pointer-events-none" />

      {/* Chat Window */}
      <main className="relative z-10 m-auto w-full max-w-5xl h-[92vh] flex flex-col rounded-3xl backdrop-blur-xl bg-white/40 border border-white/60 shadow-[0_10px_50px_-15px_rgba(0,0,0,0.25)] overflow-hidden">
        {/* Header */}
        <header className="relative h-16 flex items-center px-4 sm:px-6 justify-between bg-white/70 border-b border-white/60">
          <button
            onClick={() => navigate(-1)}
            className="text-xs sm:text-sm px-3 py-1.5 rounded-full transition shadow-sm border
                       bg-white/90 hover:bg-white text-violet-900 border-violet-200/70"
          >
            ← Back
          </button>

          <h1 className="font-semibold text-sm sm:text-base tracking-wide">
            <span className="mr-1.5">🤖</span>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-700 via-violet-700 to-fuchsia-700">
              Cura AI
            </span>
            <span className="text-slate-500"> — a Lung Innovations Network Initiative</span>
          </h1>

          <div className="min-w-[140px] text-right">
            <AnimatePresence initial={false}>
              {busy ? (
                <motion.span
                  key="thinking"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="text-[11px] sm:text-xs text-slate-600 inline-flex items-center gap-2"
                >
                  <span>Thinking… ({remaining}s)</span>
                  <TypingDots />
                </motion.span>
              ) : (
                <motion.span
                  key="ready"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  className="text-[11px] sm:text-xs text-slate-500"
                >
                  Ready
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* Progress bar (brand gradient) */}
          <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-slate-200/60 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-500"
              initial={{ width: 0 }}
              animate={{ width: busy ? progressWidth : 0 }}
              transition={{ type: "tween", ease: "easeOut", duration: 0.3 }}
            />
          </div>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
          {messages.length === 0 && !input.trim() && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center text-center text-lg sm:text-xl font-semibold pointer-events-none px-4 h-full"
            >
              <span className="text-slate-900">
                Hi, I’m{" "}
                <span className="bg-gradient-to-r from-indigo-700 via-violet-700 to-fuchsia-700 bg-clip-text text-transparent">
                  Cura
                </span>
                . What can I help you with today?
              </span>
            </motion.div>
          )}

          {messages.map((m, i) => {
            const isUser = m.role === "user";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`max-w-3xl ${isUser ? "ml-auto" : ""}`}
              >
                <div
                  className={[
                    "px-4 py-3 rounded-2xl shadow-sm leading-relaxed border",
                    isUser
                      ? // User bubble: elegant gradient (no black)
                        "text-white border-transparent bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600"
                      : // Assistant bubble: soft tinted white
                        "text-slate-900 border-white bg-gradient-to-br from-white/95 to-indigo-50/60",
                  ].join(" ")}
                  style={{ backdropFilter: isUser ? undefined : "blur(8px)" }}
                >
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              </motion.div>
            );
          })}

          {/* Assistant typing bubble */}
          <AnimatePresence>
            {busy && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="max-w-3xl"
              >
                <div className="px-4 py-3 rounded-2xl shadow-sm border border-white
                                bg-gradient-to-br from-white/95 to-indigo-50/60 text-slate-800">
                  <span className="mr-2">Hang tight</span>
                  <TypingDots />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div ref={endRef} />
        </div>

        {/* Composer (button as sibling to avoid overlap) */}
        <div className="border-t border-white/60 bg-white/65 p-4">
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <textarea
              ref={textRef}
              rows={1}
              className="flex-1 min-h-[44px] p-3 rounded-2xl border border-indigo-200/70
                         bg-white/90 text-slate-900 placeholder:text-slate-500
                         focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-transparent
                         shadow-sm backdrop-blur-sm transition resize-none overflow-y-auto"
              style={{ scrollbarGutter: "stable" }}
              placeholder="Ask about Inclusion/Exclusion criteria…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />

            <button
              type="button"
              onClick={sendMessage}
              disabled={!input.trim() || busy}
              aria-label="Send"
              className="h-10 px-4 inline-flex items-center gap-1.5 rounded-xl text-sm text-white
                         shadow-sm ring-1 ring-black/5 transition select-none
                         bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600
                         hover:brightness-110 active:scale-[0.98]
                         disabled:opacity-50 disabled:cursor-not-allowed"
              title="Send (Enter)"
            >
              {busy ? (
                <TypingDots />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M3 11L21 3L13 21L11 13L3 11Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
              )}
              <span>Send</span>
            </button>
          </div>

          {/* Footer hint */}
          <div className="max-w-3xl mx-auto mt-2 text-[11px] text-slate-500 flex justify-between">
            <span>
              Press <kbd className="px-1.5 py-0.5 bg-slate-200/70 rounded">Enter</kbd> to send •{" "}
              <kbd className="px-1.5 py-0.5 bg-slate-200/70 rounded">Shift</kbd> +{" "}
              <kbd className="px-1.5 py-0.5 bg-slate-200/70 rounded">Enter</kbd> for a new line
            </span>
            {busy && <span>Estimated {remaining}s left</span>}
          </div>
        </div>
      </main>
    </div>
  );
}
